import { makeAutoObservable, runInAction } from 'mobx'
import InputStore from '../../../stores/InputStore'
import DriverService from '../../../services/DriverService'
import { MAINTENANCE_MODE } from '../../../routing/paths'
import { string } from 'yup'

class CaselessCheckoutCreditCardStore {
  constructor(licenseStore) {
    this.isLoading = false
    this.firstName = new InputStore(
      string()
        .matches(/^[\p{L} ,.'-]+$/u, 'Invalid First Name. Only letters are allowed')
        .required('First Name is required')
    )
    this.lastName = new InputStore(
      string()
        .matches(/^[\p{L} ,.'-]+$/u, 'Invalid Last Name. Only letters are allowed')
        .required('Last Name is required')
    )
    this.addressLine = new InputStore(string().required('Address line 1 is required'))
    this.addressLine2 = new InputStore(string())
    this.city = new InputStore(string().required('City is required'))
    this.state = new InputStore(string().required('State is required'))
    this.zipCode = new InputStore(
      string()
        .matches(/^[A-Z0-9]+$/, 'Invalid Zip Code. Only numbers and uppercase letters are allowed')
        .required('ZIP is required')
    )
    this.country = new InputStore(string().required('Country is required'))
    this.phone = new InputStore(
      string()
        .matches(/^(\+\d{6,}|\d{6,})$/, 'Invalid Phone Number. Please enter a valid phone number')
        .required('Phone Number is required')
    )
    this.email = new InputStore(
      string().email('Email must be a valid email address').required('Email is required')
    )
    this.month = new InputStore(string().required('Month is required'))
    this.year = new InputStore(string().required('Year is required'))
    this.agreedTerms = false
    this.globalError = ''
    this.termsError = false
    this.usaeError = null
    this.paymentMethodToken = null

    makeAutoObservable(this)

    this.licenseStore = licenseStore
    this.driverService = new DriverService()
  }

  changeFirstName(val) {
    this.firstName.setValue(val)
  }

  changeLastName(val) {
    this.lastName.setValue(val)
  }

  changeAddressLine(val) {
    this.addressLine.setValue(val)
  }

  changeAddressLine2(val) {
    this.addressLine2.setValue(val)
  }

  changeCity(val) {
    this.city.setValue(val)
  }

  changeState(val) {
    this.state.setValue(val)
  }

  changeZipCode(val) {
    this.zipCode.setValue(val)
  }

  changeCountry(val) {
    this.country.setValue(val)
  }

  changePhone(val) {
    this.phone.setValue(val)
  }

  changeEmail(val) {
    this.email.setValue(val)
  }

  changeMonth(val) {
    this.month.setValue(val)
  }

  changeYear(val) {
    this.year.setValue(val)
  }

  setPaymentMethodToken(val) {
    this.paymentMethodToken = val
  }

  toggleTerms() {
    this.agreedTerms = !this.agreedTerms
    this.termsError = false
  }

  clearErrors() {
    this.globalError = ''
    this.termsError = false
    this.usaeError = false
    this.usaeToken = null
    this.firstName.clearError()
    this.lastName.clearError()
    this.addressLine.clearError()
    this.addressLine2.clearError()
    this.city.clearError()
    this.state.clearError()
    this.zipCode.clearError()
    this.country.clearError()
    this.phone.clearError()
    this.email.clearError()
    this.month.clearError()
    this.year.clearError()
  }

  async validate() {
    this.clearErrors()
    let isValid = true

    if (!(await this.firstName.validate())) {
      isValid = false
    }

    if (!(await this.lastName.validate())) {
      isValid = false
    }

    if (!(await this.addressLine.validate())) {
      isValid = false
    }

    if (!(await this.addressLine2.validate())) {
      isValid = false
    }

    if (!(await this.city.validate())) {
      isValid = false
    }

    if (!(await this.state.validate())) {
      isValid = false
    }

    if (!(await this.zipCode.validate())) {
      isValid = false
    }

    if (!(await this.country.validate())) {
      isValid = false
    }

    if (!(await this.phone.validate())) {
      isValid = false
    }

    if (!(await this.email.validate())) {
      isValid = false
    }

    if (!this.agreedTerms) {
      runInAction(() => {
        this.termsError = true
      })
      isValid = false
    }

    if (!(await this.month.validate())) {
      isValid = false
    }

    if (!(await this.year.validate())) {
      isValid = false
    }

    return isValid
  }

  async checkout(recaptchaToken) {
    if (await this.validate()) {
      runInAction(() => {
        this.isLoading = true
      })

      try {
        const license = await this.driverService.checkoutCC(
          this.firstName.value,
          this.lastName.value,
          this.addressLine.value,
          this.addressLine2.value,
          this.city.value,
          this.state.value,
          this.zipCode.value,
          this.country.value,
          this.phone.value,
          this.email.value,
          this.licenseStore.license.number,
          this.licenseStore.license.state,
          this.licenseStore.selectedCitationsIds,
          this.licenseStore.operationId,
          this.paymentMethodToken,
          recaptchaToken
        )

        runInAction(() => {
          this.isLoading = false
        })

        return license
      } catch (e) {
        runInAction(() => {
          if (e?.response?.status === 503) {
            window.location.href = `${MAINTENANCE_MODE}?phone=${e?.response?.data.phone}`
          } else {
            const displayedError = this.parseRequestErrors(e.response?.data?.errors || {})

            if (!displayedError) {
              this.serverError =
                'Something went wrong, please check the provided data and try again.'
            }
          }
          this.isLoading = false
        })
      }
    }

    return false
  }

  parseRequestErrors(messages) {
    const keys = Object.keys(messages)
    let displayedError = false

    keys.forEach((key) => {
      const [error] = messages[key]

      switch (key) {
        case 'firstname':
          this.firstName.setError(true, error)
          displayedError = true
          break

        case 'lastname':
          this.lastName.setError(true, error)
          displayedError = true
          break

        case 'billing_address.country':
          this.country.setError(true, error)
          displayedError = true
          break

        case 'billing_address.state':
          this.state.setError(true, error)
          displayedError = true
          break

        case 'billing_address.city':
          this.city.setError(true, error)
          displayedError = true
          break

        case 'billing_address.postalcode':
          this.zipCode.setError(true, error)
          displayedError = true
          break

        case 'billing_address.street':
          this.addressLine.setError(true, error)
          displayedError = true
          break

        case 'billing_address.street2':
          this.addressLine2.setError(true, error)
          displayedError = true
          break

        case 'phone':
          this.phone.setError(true, error)
          displayedError = true
          break

        case 'email':
          this.email.setError(true, error)
          displayedError = true
          break

        case 'card':
          this.usaeError = 'Invalid credit card data'
          displayedError = true
          break

        case 'month':
          this.month.setError(true, error)
          displayedError = true
          break

        case 'year':
          this.year.setError(true, error)
          displayedError = true
          break

        default:
          break
      }
    })

    return displayedError
  }
}

export default CaselessCheckoutCreditCardStore
